<template>
  <div id="scrollTop" class="Project h-screen bg-white text-black pt-12">
    <h1 class="text-transparent fixed">Architect Serving Launceston and Devonport</h1>
    <!-- video modal -->
    <div
      v-if="project.video_url"
      @click="videoOpen=false"
      class="z-30 fixed h-screen w-full items-center justify-center transition-all duration-500"
      :class="{
        'videoModalBackground opacity-100 flex':videoOpen,
        'opacity-0 hidden':!videoOpen,
      }"
    >
      <div v-if="videoOpen" class="absolute p-4 md:p-12 w-full text-white rounded-xl">
        <div class="mb-12 md:mb-24">
          <iframe class="w-full" :height="videoHeight" :src="project.video_url" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
        </div>
      </div>
    </div>

    <!-- mobile top half -->
    <div class="flex flex-wrap md:hidden w-full pt-20 md:pt-10 pb-8">
      <div class="w-1/2 pl-8">
        <h5 class="leading-none">{{project.name}}</h5>
        <h6 class="text-xs font-hairline">Viewing</h6>
      </div>
      <div class="w-1/2 text-right flex items-end justify-end select-none pr-6">
        <div v-if="nextProject.name" @click="goToNextProject" class="cursor-pointer">
          <h6 class="text-xs font-hairline">Next</h6>
          <h6 class="text-base leading-none">{{nextProject.name}}</h6>
        </div>
      </div>
      <div
        :style="{ backgroundImage: 'url(' + project.heroSrc + ')' }"
        class="w-full h-40 bg-cover bg-center bg-no-repeat my-8"
      >
      </div>
      <div class="flex md:hidden w-full px-8 justify-center">
        <Accordian :accordianData="accordianData" />
      </div>
    </div>

    <!-- desktop top half -->
    <div class="hidden md:flex flex-col h-full min-h-full">
      <div class="flex flex-wrap flex-1">
        <div
          :style="{ backgroundImage: 'url(' + project.heroSrc + ')' }"
          class="flex-1 bg-cover bg-center bg-no-repeat flex items-end"
        >
          <p
            @click="scrollToBottom"
            class="text-white ml-10 mb-8 cursor-pointer uppercase text-xs tracking-widest font-sansBold font-medium"
          >
            Scroll Down
          </p>
        </div>
        <div class="flex w-72 lg:w-112 pl-8 pt-6 flex-wrap content-between">
          <div class="w-3/4">
            <h3 class="leading-none font-bold">{{project.name}}</h3>
            <p class="uppercase font-hairline">Viewing</p>
          </div>
          <div class="w-full">
            <NextButton v-if="nextProject.name" @onClick="goToNextProject" :project="nextProject" />
          </div>
        </div>
      </div>
      <div class="flex p-10 justify-center">
        <div class="px-5 lg:px-10 py-2 h-full w-1/4 border-black">
          <div class="text-right">
            <h6 class="text-sm uppercase">Project</h6>
            <h5 class="text-lg lg:text-2xl">{{project.type}}</h5>
            <p class="uppercase">{{project.location}}</p>
          </div>
        </div>
        <div class="px-5 lg:px-10 py-2 h-full w-1/3 border-l border-black">
          <p class="text-xs xl:text-base leading-6 xl:leading-9">
            {{project.overview}}
          </p>
        </div>
        <div class="px-5 lg:px-10 py-2 h-full w-1/4 border-l border-black">
          <h6 class="text-sm uppercase">Materials</h6>
          <p class="text-xs xl:text-base leading-6 xl:leading-9">
            {{project.materials}}
          </p>
        </div>
        <div class="px-5 lg:px-10 py-2 h-full w-1/4 border-l border-black">
          <h6 class="text-sm uppercase">Building</h6>
          <p class="text-xs xl:text-base leading-6 xl:leading-9">
            {{project.building}}
          </p>
        </div>
        <div class="px-5 lg:px-10 py-2 h-full w-1/4 border-l border-black">
          <h6 class="text-sm uppercase">Photography</h6>
          <p class="text-xs xl:text-base leading-6 xl:leading-9">
            {{project.photography}}
          </p>
        </div>
      </div>
    </div>

    <div id="scrollBottom" class="w-full">
      <div class="w-full flex flex-wrap pl-0 md:pl-10">
        <div
          class="flex-1 h-full pl-3 pr-3 md:pl-0 md:pr-0 pb-10"
        >
          <div :style="gridDivStyle" id="galleryDiv" class="h-full gap-1 md:gap-5">
            <div
              :style="{ backgroundImage: 'url(' + project.video_thumbnail + ')' }"
              class="relative bg-cover bg-no-repeat bg-center bg-gray-400"
              id="videoThumbnail"
            >
              <div
                v-if="project.video_url"
                @click="videoOpen=true"
                class="
                w-full h-full
                flex
                absolute
                top-0
                left-0
                bg-black
                items-center
                justify-center
                cursor-pointer
                opacity-50
                hover:opacity-25
                transition duration-300 ease-in-out
                "
              >
                <div class="text-white">
                  <PlayIcon />
                </div>
              </div>
            </div>
            <template
              v-for="(img, index) in gallery"
            >
              <div
                class="bg-gray-400"
                :id="'image'+index"
                :key="index"
                v-if="index<8"
              >
                <img class="w-full h-full object-cover" :src="img.imageSrc" alt="Architect Launceston Architect Devonport" />
              </div>
            </template>
          </div>
        </div>
        <div class="flex w-full md:w-72 lg:w-112 p-8 flex-wrap content-between">
          <p class="w-full text-xs">
            {{project.description}}
          </p>
          <div class="hidden md:flex w-full">
            <div class="w-1/3">
              <CustomButtonTwo @onClick="scrollToTop" dark>
                Top
              </CustomButtonTwo>
            </div>
            <div class="w-2/3 ml-8">
              <CustomButtonThree v-if="nextProject.name" @onClick="goToNextProject" dark>
                Next
              </CustomButtonThree>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex md:hidden w-full pb-24 pl-8 items-end">
      <div class="w-20">
        <CustomButtonTwo @onClick="scrollToTop" dark>
          Top
        </CustomButtonTwo>
      </div>
      <div class="w-1/2 flex-1">
        <NextButton @onClick="goToNextProject" :project="nextProject" />
      </div>
    </div>

  </div>
</template>

<script>
// @ is an alias to /src
import axios from 'axios'
import NextButton from '@/components/NextButton.vue'
import CustomButtonTwo from '@/components/elements/CustomButtonTwo.vue'
import CustomButtonThree from '@/components/elements/CustomButtonThree.vue'
import Accordian from '@/components/elements/Accordian.vue'
import PlayIcon from '@/components/icons/PlayIcon.vue'
var VueScrollTo = require('vue-scrollto')

export default {
  name: 'project',
  components: {
    NextButton,
    CustomButtonTwo,
    CustomButtonThree,
    Accordian,
    PlayIcon
  },
  metaInfo () {
    return {
      title: 'Architecture in Devonport & Launceston | Starbox Architecture',
      meta: [
        {
          name: 'description',
          content: 'Starbox Architecture is a team of Devonport-based architects that serve individuals around Launceston. Visit us online to get started!'
        },
        {
          name: 'keywords',
          content: 'starbox,architecture,starbox architecture,tasmania,tasmanian,tasmanian architecture,tasmania architecture'
        }
      ]
    }
  },
  data () {
    return {
      videoOpen: false,
      project: {},
      nextProject: {},
      gallery: [],
      accordianData: [
        {
          name: 'Project',
          content: ''
        },
        {
          name: 'Overview',
          content: ''
        },
        {
          name: 'Materials',
          content: ''
        },
        {
          name: 'Building',
          content: ''
        },
        {
          name: 'Photography',
          content: ''
        }
      ]
    }
  },
  computed: {
    videoHeight () {
      var toReturn = '300'
      if (this.$mq === 'sm') toReturn = '300'
      else if (this.$mq === 'md') toReturn = '400'
      else if (this.$mq === 'lg') toReturn = '400'
      else if (this.$mq === 'xl') toReturn = '400'
      else if (this.$mq === 'xxl') toReturn = '800'
      else if (this.$mq === 'xxxl') toReturn = '800'
      return toReturn
    },
    gridDivStyle () {
      var toReturn = {}

      var height = ''
      if (this.$mq === 'sm') height = '800px'
      else if (this.$mq === 'md') height = '1300px'
      else if (this.$mq === 'lg') height = '1600px'
      else if (this.$mq === 'xl') height = '2550px'
      else if (this.$mq === 'xxl') height = '2550px'
      else if (this.$mq === 'xxxl') height = '2550px'

      toReturn = {
        'height': height
      }
      return toReturn
    }
  },
  methods: {
    goToNextProject () {
      this.scrollToTop()
      this.$router.replace('/project/' + this.nextProject.id)
    },
    scrollToTop () {
      VueScrollTo.scrollTo('#scrollTop', '500', {})
    },
    scrollToBottom () {
      VueScrollTo.scrollTo('#scrollBottom', '500', {})
    },
    getProject () {
      var toReturn = {}
      const updater = new URLSearchParams()
      updater.append('id', this.$route.params.id)
      axios.post(process.env.VUE_APP_ROOT_API + '/project', updater).then(response => {
        var data = response.data
        toReturn = {
          id: data.id,
          name: data.name,
          type: data.type,
          description: data.description,
          location: data.location,
          overview: data.overview,
          materials: data.materials,
          building: data.building,
          photography: data.photography,
          video_url: data.video_url
        }
        if (data.hero_image) toReturn.heroSrc = data.hero_image.path
        if (data.video_thumbnail) toReturn.video_thumbnail = data.video_thumbnail.path

        this.project = toReturn

        this.accordianData = [
          {
            name: 'Project',
            content: `
              <div class="text-left">
                <h5 class="">` + data.type + `</h5>
                <p class="uppercase">` + data.location + `</p>
              </div>
            `
          },
          {
            name: 'Overview',
            content: data.overview
          },
          {
            name: 'Materials',
            content: data.materials
          },
          {
            name: 'Building',
            content: data.building
          },
          {
            name: 'Photography',
            content: data.photography
          }
        ]

        data = response.data.gallery
        this.gallery = []
        for (var i = 0; i < data.length; i++) {
          this.gallery.push(
            {
              imageSrc: data[i].path
            }
          )
        }

        data = response.data.nextProject
        if (data) {
          toReturn = {
            id: data.id,
            name: data.name
          }
          if (data.hero_image) toReturn.heroSrc = data.hero_image.path
          this.nextProject = toReturn
        }
      }).catch(error => {
        console.log('Error', error)
      })
    }
  },
  mounted () {
    this.getProject()
  },
  beforeDestroy () {
  }
}
</script>

<style lang="scss">
  $sm: '640px';
  $md: '768px';
  $lg: '1024px';
  $xl: '1280px';
  $xxl: '1920px';

  .Project {
    .videoModalBackground {
      background-color: rgba(0, 0, 0, 0.9);
    }

    #galleryDiv
    {
        display: grid;
        // grid-gap: 20px;
        grid-template-columns: 0.8fr 0.2fr 1fr;
        grid-template-rows: 4.5fr 2.5fr 1fr 1.5fr 2.5fr;
        grid-template-areas:
        "image0 image0 image0"
        "image1 videoThumbnail videoThumbnail"
        "image1 image2 image2"
        "image3 image2 image2"
        "image4 image4 image5"
        "image6 image7 image7";
    }
    #videoThumbnail
    {
      grid-area: videoThumbnail;
    }
    #image0
    {
      grid-area: image0;
    }
    #image1
    {
      grid-area: image1;
    }
    #image2
    {
      grid-area: image2;
    }
    #image3
    {
      grid-area: image3;
    }
    #image4
    {
      grid-area: image4;
    }
    #image5
    {
      grid-area: image5;
    }
    #image6
    {
      grid-area: image6;
    }
    #image7
    {
      grid-area: image7;
    }

  }
</style>
